import {ChevronRightIcon, QuestionBubbleIcon, RocketIcon, TasklistIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Label, Tooltip} from '@primer/react'
import {useRef, useState} from 'react'

import {
  APPROVAL_MODES,
  type SessionApprovalMode,
  type SessionEffectiveApprovalMode,
} from '@github-ui/ahp-relay/permission-mode'
import {APPROVAL_MODE_PRESENTATION} from '@github-ui/copilot-shared-ui/utils/approval-mode-presentation'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import styles from './CLIModePicker.module.css'

export type CLIAgentMode = 'interactive' | 'plan' | 'autopilot'

const MODE_INFO: Record<CLIAgentMode, {icon: React.ComponentType; label: string; description: string}> = {
  interactive: {
    icon: QuestionBubbleIcon,
    label: 'Interactive',
    description: 'Step-by-step collaboration',
  },
  plan: {
    icon: TasklistIcon,
    label: 'Plan',
    description: 'Plan first, execute when ready',
  },
  autopilot: {
    icon: RocketIcon,
    label: 'Autopilot',
    description: 'End-to-end execution without interruption',
  },
}

const MODES: CLIAgentMode[] = ['interactive', 'plan', 'autopilot']

interface CLIModePickerProps {
  selectedMode: CLIAgentMode
  onModeChange: (mode: CLIAgentMode) => void
  availableModes?: readonly CLIAgentMode[]
  selectedApprovalMode?: SessionApprovalMode
  effectiveApprovalMode?: SessionEffectiveApprovalMode
  onApprovalModeChange?: (mode: SessionApprovalMode) => void | Promise<void>
  availableApprovalModes?: readonly SessionApprovalMode[]
  approvalModeMutable?: boolean
  approvalModePending?: boolean
  approvalModeError?: string
  disabled?: boolean
  portalContainerName?: string
}

export function CLIModePicker({
  selectedMode,
  onModeChange,
  availableModes = MODES,
  selectedApprovalMode,
  effectiveApprovalMode,
  onApprovalModeChange,
  availableApprovalModes = APPROVAL_MODES,
  approvalModeMutable = true,
  approvalModePending,
  approvalModeError,
  disabled,
  portalContainerName,
}: CLIModePickerProps) {
  const enabledModes = isFeatureEnabled('one_copilot_plan_mode')
    ? availableModes
    : availableModes.filter(mode => mode !== 'plan')
  const effectiveSelectedMode = enabledModes.includes(selectedMode) ? selectedMode : (enabledModes[0] ?? selectedMode)
  const currentInfo = MODE_INFO[effectiveSelectedMode]
  const [open, setOpen] = useState(false)
  const [permissionsOpen, setPermissionsOpen] = useState(false)
  const [compact, setCompact] = useState(false)
  const permissionsAnchorRef = useRef<HTMLLIElement>(null)
  const overlaySide = portalContainerName ? 'outside-top' : undefined
  // A session can enforce a permissive mode the client may not change, and can
  // advertise no selectable modes at all. Surfacing the row whenever the host
  // reports any approval signal keeps "tools run without asking" visible rather
  // than letting it disappear behind a missing or locked selection.
  const showToolPermissions =
    selectedApprovalMode !== undefined || (effectiveApprovalMode !== undefined && effectiveApprovalMode !== 'unknown')
  const selectedApprovalInfo = selectedApprovalMode ? APPROVAL_MODE_PRESENTATION[selectedApprovalMode] : undefined
  const effectiveApprovalInfo =
    effectiveApprovalMode && effectiveApprovalMode !== 'unknown'
      ? APPROVAL_MODE_PRESENTATION[effectiveApprovalMode]
      : undefined
  const effectiveApprovalLabel =
    effectiveApprovalMode === 'unknown'
      ? 'Unknown'
      : effectiveApprovalInfo && effectiveApprovalMode !== selectedApprovalMode
        ? effectiveApprovalInfo.label
        : undefined
  const approvalLabel =
    selectedApprovalInfo && effectiveApprovalLabel
      ? `${selectedApprovalInfo.label}, ${effectiveApprovalLabel} active`
      : (selectedApprovalInfo?.label ?? effectiveApprovalLabel)
  // Always offer the active mode as a row so the menu can never open empty, even
  // when the host advertises nothing as available.
  const approvalChoices = APPROVAL_MODES.filter(
    mode => availableApprovalModes.includes(mode) || mode === selectedApprovalMode,
  )
  const approvalModeReadOnly = !onApprovalModeChange || !approvalModeMutable
  const updateTooltip = (target: HTMLButtonElement) => {
    setCompact(current => {
      const next = target.getBoundingClientRect().width <= 32
      return current === next ? current : next
    })
  }
  const closePermissionsMenu = () => {
    setPermissionsOpen(false)
    setOpen(false)
  }
  const changeApprovalMode = async (mode: SessionApprovalMode) => {
    try {
      await onApprovalModeChange?.(mode)
      closePermissionsMenu()
    } catch {
      // The caller presents the mutation error and retains the host-selected value.
    }
  }

  const menu = (
    <ActionMenu
      open={open}
      onOpenChange={nextOpen => {
        if (disabled) return
        setOpen(nextOpen)
        if (!nextOpen) setPermissionsOpen(false)
      }}
    >
      <Tooltip text={disabled ? 'Switching mode...' : compact ? `Mode: ${currentInfo.label}` : 'Mode'}>
        <ActionMenu.Button
          className={styles.menuButton}
          size="medium"
          variant="invisible"
          trailingAction={TriangleDownIcon}
          aria-label={`Select CLI agent mode, current: ${currentInfo.label}`}
          inactive={disabled}
          aria-disabled={disabled}
          data-responsive-compact-control="cli-mode"
          onPointerOverCapture={event => updateTooltip(event.currentTarget)}
          onFocusCapture={event => updateTooltip(event.currentTarget)}
        >
          <span className={styles.compactIcon} data-responsive-compact-icon aria-hidden="true">
            <currentInfo.icon />
          </span>
          <span className={styles.buttonLabel} data-responsive-compact-label>
            {currentInfo.label}
          </span>
        </ActionMenu.Button>
      </Tooltip>
      <ActionMenu.Overlay width="auto" portalContainerName={portalContainerName} side={overlaySide}>
        <ActionList>
          <ActionList.Group selectionVariant="single">
            <ActionList.GroupHeading>Mode</ActionList.GroupHeading>
            {enabledModes.map(mode => {
              const info = MODE_INFO[mode]
              return (
                <ActionList.Item
                  key={mode}
                  selected={mode === effectiveSelectedMode}
                  onSelect={() => onModeChange(mode)}
                >
                  <ActionList.LeadingVisual>
                    <info.icon />
                  </ActionList.LeadingVisual>
                  {info.label}
                  <ActionList.Description variant="block">{info.description}</ActionList.Description>
                </ActionList.Item>
              )
            })}
          </ActionList.Group>
          {showToolPermissions && (
            <>
              <ActionList.Divider />
              <ActionList.Group selectionVariant={false}>
                <ActionList.Item
                  ref={permissionsAnchorRef}
                  aria-haspopup="true"
                  aria-expanded={permissionsOpen}
                  onSelect={event => {
                    event.preventDefault()
                    setPermissionsOpen(true)
                  }}
                >
                  Tool permissions
                  <ActionList.TrailingVisual>
                    <span>{approvalLabel}</span>
                    <ChevronRightIcon />
                  </ActionList.TrailingVisual>
                </ActionList.Item>
              </ActionList.Group>
            </>
          )}
        </ActionList>
        {showToolPermissions && (
          <ActionMenu anchorRef={permissionsAnchorRef} open={permissionsOpen} onOpenChange={setPermissionsOpen}>
            <ActionMenu.Overlay
              width="medium"
              side="outside-right"
              align="start"
              portalContainerName={portalContainerName}
            >
              <ActionList selectionVariant="single">
                {effectiveApprovalLabel && (
                  <ActionList.Group>
                    <ActionList.GroupHeading>Currently enforced: {effectiveApprovalLabel}</ActionList.GroupHeading>
                  </ActionList.Group>
                )}
                {approvalModeReadOnly && (
                  <ActionList.Group>
                    <ActionList.GroupHeading>Set by this session and cannot be changed here</ActionList.GroupHeading>
                  </ActionList.Group>
                )}
                {approvalChoices.map(mode => {
                  const info = APPROVAL_MODE_PRESENTATION[mode]
                  return (
                    <ActionList.Item
                      key={mode}
                      selected={mode === selectedApprovalMode}
                      disabled={approvalModeReadOnly || approvalModePending}
                      onSelect={event => {
                        event.preventDefault()
                        void changeApprovalMode(mode)
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <span />
                      </ActionList.LeadingVisual>
                      <span>
                        {info.label} {info.experimental && <Label variant="secondary">Experimental</Label>}
                      </span>
                      <ActionList.Description variant="block">{info.description}</ActionList.Description>
                    </ActionList.Item>
                  )
                })}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  return (
    <>
      {menu}
      {approvalModeError && (
        <span className={styles.error} role="alert">
          {approvalModeError}
        </span>
      )}
    </>
  )
}
